import {lazy, Suspense, useCallback, useEffect, useMemo} from "react";
import {Container} from "Components/shared";
import {useDispatch, useSelector} from "react-redux";
import {injectReducer} from "Store";
import reducer from "../Cart/store";
import {clearRemovedItems, setCart} from "./store/cartSlice";
import PubSub, {Message} from "Utils/pubSub";
import {liquidGetCart} from "./store/thunks";
import {selectCart} from "./store/cartSelectors";
import isEqual from "fast-deep-equal/es6";
import {setAddress, setCartId} from "../store/elementsSlice";
import {liquidClient} from "../store/thunks";
import {TrackingService} from "../../../Services/TrackingService";
import {useIsDemo} from "../../../Utils/hooks/useIsDemo";
import {CustomerPlacement} from "../../../Utils/liquidUtils";
import { useStandaloneCart } from "./hooks/useStandaloneCart";

injectReducer('cart', reducer)

const CartContainer = props => {

  const dispatch = useDispatch()
  const cart = useSelector(selectCart, isEqual)
  const isDemo = useIsDemo()
  const {
    configs,
    dynamicStyles,
    liquid,
    liquidToken,
    loading
  } = props

  const { text } = configs

  useStandaloneCart({liquid});

  useEffect(() => {
    // Initialize SDK if not ready, otherwise load cart data
    if (!liquid && !loading && liquidToken) {
      dispatch(liquidClient(liquidToken))
    } else if (liquid) {
      dispatch(liquidGetCart())
    }
  }, [dispatch, liquid, loading, liquidToken])

  useEffect(() => {
    const cartUpdate = ({ cart }) => {
      dispatch(setCartId(cart.id))
      dispatch(setCart(cart))
    }
    const cartUpdateId = ({ cart }) => dispatch(setCartId(cart.id))
    const addressUpdate = ({ address }) => dispatch(setAddress(address))
    const cartAdd = () => dispatch(clearRemovedItems())

    PubSub.subscribe(Message.CartUpdated, cartUpdate);
    PubSub.subscribe(Message.CartLoaded, cartUpdateId);
    PubSub.subscribe(Message.AddToCart, cartAdd);
    PubSub.subscribe(Message.AddressChanged, addressUpdate)

    return () => {
      PubSub.unsubscribe(Message.CartUpdated, cartUpdate)
      PubSub.unsubscribe(Message.CartLoaded, cartUpdateId)
      PubSub.unsubscribe(Message.AddToCart, cartAdd)
      PubSub.unsubscribe(Message.AddressChanged, addressUpdate)
    }
  }, [dispatch])

  useEffect(() => {
    const subscription = PubSub.subscribe(Message.CartItemAdd, async (updateCart) => {
      const cart = await liquid.cart({...updateCart,
      cartItems: updateCart.cartItems.map(item => ({
        ...item,
        customerPlacement: item.customerPlacement ?? CustomerPlacement.STANDARD,
      }))
      })
      PubSub.publish(Message.CartUpdated, {cart})
      PubSub.publish(Message.AddToCart)
    });
    return () => subscription.unsubscribe()
  }, [liquid]);

  useEffect(() => {
    const trackViewCart = ({ show }) => show && TrackingService.trackViewCart(cart);
    PubSub.subscribe(Message.CartToggled, trackViewCart);

    return () => PubSub.unsubscribe(Message.CartToggled, trackViewCart);
  }, [cart])

  const onClose = useCallback( () => {
    if(isDemo) {
      return
    }
    dispatch(clearRemovedItems())
    PubSub.publish(Message.ToggleCart)
  }, [dispatch, isDemo])

  const Element = useMemo(() => {
    if (!cart.loading) return lazy(() => import('./Cart'))

    return lazy(() => import("Components/shared/loaders/CartSkeleton"))
  }, [cart.loading])


  return (
    <Container
      className={`w-full justify-center items-center flex max-w-[600px] h-[100vh]`}
      style={{
        fontFamily: text.body.font,
        color: text.body.color
      }}
    >
      <Suspense>
        <Element
          {...configs}
          cart={cart}
          dynamicStyles={dynamicStyles}
          onClose={onClose}
        />
      </Suspense>
    </Container>
  )
}


export default CartContainer
